import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { ChartConfig }                         from '../chart';
import { ChartHelper }                         from '../chart-helper';

interface TreemapTile {
	x: number;
	y: number;
	width: number;
	height: number;
	fill: string;
	label: string;
	/** Truncated label drawn inside the tile ('' when the tile is too small for text). */
	displayLabel: string;
	/** Compact value drawn under the label ('' when there is no room for a second line). */
	displayValue: string;
	/** Tooltip body: exact value + share of the whole. */
	text: string;
}

/**
 * Treemap: each label/value pair becomes a tile whose AREA is proportional to its value,
 * laid out with the squarified algorithm (Bruls/Huizing/van Wijk) so tiles stay close to
 * square and remain comparable. Values must be positive; non-positive entries are dropped.
 * Tiles big enough get their label (and value) inked inside; every tile has a hover tooltip.
 */
@Component({
	standalone:  false,
	selector:    '[cs-treemap-chart]',
	templateUrl: './treemap-chart.component.html'
})
export class CsTreemapChartComponent implements OnInit, OnChanges {

	@Input() config: ChartConfig;

	tiles: TreemapTile[] = [];

	private isDataReady() {
		return this.config?.data?.datasets?.length && this.config.data.datasets[0].data?.length;
	}

	ngOnInit() {
		setTimeout(() => {
			if (this.isDataReady()) { this.calculate(); }
		}, 1);
	}

	ngOnChanges(changes: any) {
		if (changes.hasOwnProperty('config') && this.isDataReady()) {
			this.calculate();
		}
	}

	private calculate() {
		const w = this.config.chartWidth  - this.config.xPadding * 2;
		const h = this.config.chartHeight - this.config.yPadding * 2;
		if (!w || !h || w < 10 || h < 10) { return; }

		// A treemap encodes value as area — zero/negative values have none. Sort descending:
		// squarify expects it, and the reading order (big → small) matches the visual one.
		const items = this.config.data.datasets[0].data
			.map((value, i) => ({ value, label: String(this.config.data.labels?.[i] ?? '') }))
			.filter(d => Number.isFinite(d.value) && d.value > 0)
			.sort((a, b) => b.value - a.value);
		if (!items.length) { this.tiles = []; return; }

		const total = items.reduce((s, d) => s + d.value, 0);
		const rects = squarify(
			items.map(d => d.value),
			{ x: this.config.xPadding, y: this.config.yPadding, width: w, height: h });

		this.tiles = rects.map((r, i) => {
			const item  = items[i];
			const share = Math.round(item.value / total * 1000) / 10;
			// Inner gutter: tiles separated by the surface showing through.
			const gap    = 1.5;
			const x      = r.x + gap, y = r.y + gap;
			const width  = Math.max(0, r.width - gap * 2);
			const height = Math.max(0, r.height - gap * 2);
			// Ink the label only when it plausibly fits (~6.5px/char at font 11); the value line
			// needs a second row of room. Tooltips carry the full story for the slivers.
			const maxChars     = Math.floor((width - 10) / 6.5);
			const displayLabel = height >= 22 && maxChars >= 3
				? (item.label.length > maxChars ? item.label.slice(0, maxChars - 1).trimEnd() + '…' : item.label)
				: '';
			const displayValue = displayLabel && height >= 38 && maxChars >= 4
				? ChartHelper.getValueFormatted(Math.round(item.value))
				: '';
			return {
				x, y, width, height,
				fill:  this.config.colors[i % this.config.colors.length],
				label: item.label,
				displayLabel,
				displayValue,
				text:  `${ChartHelper.getValueLocale(item.value)} (${share}%)`
			};
		});
	}
}

interface Rect { x: number; y: number; width: number; height: number; }

/**
 * Squarified treemap layout. `values` must be positive and sorted descending; returns one
 * rect per value, in the same order, tiling `bounds` exactly. Rows are laid along the
 * shorter side of the remaining area, and a value joins the current row only while it
 * improves (lowers) the row's worst aspect ratio.
 */
function squarify(values: number[], bounds: Rect): Rect[] {
	const result: Rect[] = [];
	const totalValue = values.reduce((s, v) => s + v, 0);
	const scale = (bounds.width * bounds.height) / totalValue;   // value → area

	let rest = { ...bounds };
	let row: number[] = [];
	let i = 0;

	const worst = (areas: number[], side: number): number => {
		const sum = areas.reduce((s, a) => s + a, 0);
		const max = Math.max(...areas);
		const min = Math.min(...areas);
		const s2  = sum * sum;
		return Math.max((side * side * max) / s2, s2 / (side * side * min));
	};

	const layoutRow = (areas: number[]) => {
		const sum  = areas.reduce((s, a) => s + a, 0);
		const horizontal = rest.width >= rest.height;   // row laid along the SHORTER side
		const thickness  = sum / (horizontal ? rest.height : rest.width);
		let offset = 0;
		for (const area of areas) {
			const len = area / thickness;
			result.push(horizontal
				? { x: rest.x, y: rest.y + offset, width: thickness, height: len }
				: { x: rest.x + offset, y: rest.y, width: len, height: thickness });
			offset += len;
		}
		rest = horizontal
			? { x: rest.x + thickness, y: rest.y, width: rest.width - thickness, height: rest.height }
			: { x: rest.x, y: rest.y + thickness, width: rest.width, height: rest.height - thickness };
	};

	while (i < values.length) {
		const area = values[i] * scale;
		const side = Math.min(rest.width, rest.height);
		if (!row.length || worst([...row, area], side) <= worst(row, side)) {
			row.push(area);
			i++;
		} else {
			layoutRow(row);
			row = [];
		}
	}
	if (row.length) { layoutRow(row); }
	return result;
}
